// src/pages/Elenco.js
import React, { useState, useEffect, useMemo } from 'react';
import '../App.css';
import './elenco.css';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Stessa mappa zone di AgendaPro
const ZONE = [
  { id: 1, label: 'Verona' },
  { id: 2, label: 'Modena' },
  { id: 3, label: 'Novara' },
  { id: 4, label: 'Milano Sud' },
  { id: 5, label: 'Milano Nord' },
];

function boolish(v) {
  const s = String(v ?? '').trim().toLowerCase();
  return ['1', 'true', 'si', 'sì', 'yes', 'y', 'on', 'ok'].includes(s);
}

function formatYMD(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

export default function Elenco() {
  const auth = useAuth();
  const user = auth?.user || {};

  const UID = useMemo(() => {
    const raw =
      user.id ??
      user.user_id ??
      user.ID ??
      auth?.user_id ??
      auth?.ID ??
      null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [user, auth]);

  const ruolo = useMemo(() => {
    const raw =
      auth?.ruolo ??
      auth?.role ??
      user.ruolo ??
      user.role ??
      'user';
    return String(raw).trim().toLowerCase();
  }, [auth, user]);

  const userZonaId = useMemo(() => {
    const raw =
      user.zona_id ??
      user.zonaId ??
      user.zone_id ??
      null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [user]);

  const [caps, setCaps] = useState({});
  const [capsLoaded, setCapsLoaded] = useState(false);

  const [appuntamenti, setAppuntamenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // paginazione lato client
  const [page, setPage] = useState(1);
  const [perPage] = useState(50);

  // Filtri
  const [filterText, setFilterText] = useState('');
  const [selectedZona, setSelectedZona] = useState(''); // id zona in stringa
  const [selectedUser, setSelectedUser] = useState('');

  // Ordinamento
  const [sortKey, setSortKey] = useState('data'); // 'data','ora','cliente',...
 const [sortDir, setSortDir] = useState('desc'); // 👈 CAMBIA asc -> desc

  // Log: stato modale
  const [logOpen, setLogOpen] = useState(false);
  const [logEvent, setLogEvent] = useState(null);
  const [logRows, setLogRows] = useState([]);
  const [logLoading, setLogLoading] = useState(false);
  const [logError, setLogError] = useState(null);

  const navigate = useNavigate();

  /* ===== 1) caps da Matrix (caps.php) ===== */
  useEffect(() => {
    let cancelled = false;

    setCapsLoaded(false);
    fetch('/preventivi/agenda/api/acl/caps.php', {
      credentials: 'include',
    })
      .then((res) => res.json().catch(() => ({})))
      .then((json) => {
        if (cancelled) return;
        const s = json?.caps || json || {};

        const next = {
          restrictAssignedOnly:
            boolish(s.restrictAssignedOnly) ||
            boolish(s.restrict_assigned_only),
          zoneLock: boolish(s.zoneLock) || boolish(s.zone_lock),
          zoneMulti: boolish(s.zoneMulti) || boolish(s.zone_multi),
        };

        setCaps(next);
      })
      .catch(() => {
        if (!cancelled) setCaps({});
      })
      .finally(() => {
        if (!cancelled) setCapsLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  /* ===== 2) Fetch appuntamenti da list_events.php ===== */
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    setLoading(true);
    setError(null);

    // ultimi 12 mesi
 // da 12 mesi fa a 30 giorni avanti
const today = new Date();

const fromD = new Date(today);
fromD.setFullYear(today.getFullYear() - 1);

const toD = new Date(today);
toD.setDate(toD.getDate() + 30);

const from = formatYMD(fromD);
const to   = formatYMD(toD);


    const params = new URLSearchParams({
      from,
      to,
      include_blocks: '0',
      viewer_id: String(UID || 0),
      viewer_role: ruolo || 'user',
    });

    fetch(
      `/preventivi/agenda/api/list_events.php?${params.toString()}`,
      {
        signal: controller.signal,
        credentials: 'include',
      }
    )
      .then((res) => res.text())
      .then((text) => {
        if (cancelled) return;
        try {
          const json = JSON.parse(text);
          if (json.status !== 'OK') {
            throw new Error(json.message || 'Errore generico');
          }

          const raw = Array.isArray(json.events) ? json.events : [];

          const rows = raw.map((row) => {
            const startRaw =
              row.start_dt ||
              row.start ||
              row.startDateTime ||
              '';
            const startStr = String(startRaw).replace(' ', 'T');
            const d = startStr ? new Date(startStr) : null;

            const data = d ? formatYMD(d) : '';
            const ora = d
              ? `${String(d.getHours()).padStart(2, '0')}:${String(
                  d.getMinutes()
                ).padStart(2, '0')}`
              : '';

            const fullName =
              row.nome_cliente ||
              row.cliente_nome ||
              row.nome ||
              row.title ||
              '';

            let nome = '';
            let cognome = '';
            if (fullName) {
              const parts = String(fullName).trim().split(/\s+/);
              nome = parts[0] || '';
              cognome = parts.slice(1).join(' ');
            }

            const indirizzo =
              row.cliente_indirizzo || row.indirizzo || '';
            const comune = row._comuneView || row.comune || '';
            const cap = row.cap || '';

            const zonaId = Number(row.zona_id || 0) || 0;
            const zonaFromConst =
              ZONE.find((z) => z.id === zonaId)?.label || '';
            const zonaLabel =
              row.zona_nome ||
              row.zona_label ||
              zonaFromConst ||
              (row.zona ? String(row.zona) : zonaId ? `Zona ${zonaId}` : '');

            const rilevatoreId =
              Number(
                row.rilevatore_id ?? row.rilevatore ?? row.rid ?? 0
              ) || 0;
            const seniorId =
              Number(row.senior_id ?? row.senior ?? row.sid ?? 0) ||
              0;

            const rilevatoreName =
              row.rilevatore_name ||
              row.rilevatore_label ||
              '';
            const seniorName = row.senior_name || row.senior_label || '';

            const preventivoId =
              row.preventivo_id ??
              row.preventivoId ??
              row.quote_id ??
              null;

            const audioUrl = row.audio_path || '';
            const status =
              row.ui_state_code || row.stato || row.ui_state || 'default';

            const history = Array.isArray(row.history) ? row.history : [];

            return {
              id: row.id ?? row.event_id ?? null,
              data,
              ora,
              nome,
              cognome,
              indirizzo,
              cap,
              comune,
              zona: zonaLabel,
              zonaId,
              utenteZona:
                rilevatoreName ||
                seniorName ||
                row.created_by_name ||
                '',
              preventivoId,
              audioUrl,
              status,
              rid: rilevatoreId,
              sid: seniorId,
              history,
            };
          });

          // Ordina per giorno/ora (default iniziale)
          rows.sort((a, b) => {
            const da = new Date(`${a.data}T${a.ora || '00:00'}`);
            const db = new Date(`${b.data}T${b.ora || '00:00'}`);
            return da - db;
          });

          setAppuntamenti(rows);
        } catch (err) {
          console.error('❌ Errore parsing:', err);
          console.error('Risposta server:', text);
          setError('Risposta non valida dal server');
        }
      })
      .catch((err) => {
        if (cancelled || err.name === 'AbortError') return;
        console.error('❌ Errore finale:', err);
        setError(err.message || 'Errore di rete');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [UID, ruolo]);

  /* ===== 3) zoneLock: blocca zona su quella dell'utente ===== */
  useEffect(() => {
    if (!capsLoaded) return;
    if (!caps.zoneLock || !userZonaId) return;
    if (selectedZona && Number(selectedZona) === userZonaId) return;
    setSelectedZona(String(userZonaId));
  }, [capsLoaded, caps.zoneLock, userZonaId, selectedZona]);

  /* ===== 5) Liste derivate / filtri ===== */

  // lista zone per la select (id + label)
  const zones = useMemo(() => {
    const idSet = new Set(
      appuntamenti.map((a) => a.zonaId).filter((id) => id > 0)
    );
    const out = [];
    idSet.forEach((id) => {
      const fromConst = ZONE.find((z) => z.id === id);
      const labelFromData =
        appuntamenti.find((a) => a.zonaId === id)?.zona || null;
      const label = fromConst?.label || labelFromData || `Zona ${id}`;
      out.push({ id, label });
    });
    out.sort((a, b) => a.id - b.id);
    return out;
  }, [appuntamenti]);

  // lista utenti della zona selezionata
  const usersInSelectedZone = useMemo(() => {
    const selId = Number(selectedZona || 0);
    return Array.from(
      new Set(
        appuntamenti
          .filter((a) => !selId || a.zonaId === selId)
          .map((a) => a.utenteZona)
          .filter(Boolean)
      )
    );
  }, [appuntamenti, selectedZona]);

  // Appuntamenti filtrati per Matrix (restrictAssignedOnly) + filtri UI
  const visibleAppointments = useMemo(() => {
    const me = UID || 0;
    const restrict = !!caps.restrictAssignedOnly;
    const selZonaId = Number(selectedZona || 0);

    return appuntamenti
      .filter((a) => {
        if (!restrict || !me) return true;
        // Matrix: se restrictAssignedOnly = ON → vedo solo rid/sid = me
        return a.rid === me || a.sid === me;
      })
      .filter((a) => {
        if (!filterText) return true;
        const term = filterText.toLowerCase();
        const safe = (v) => String(v || '').toLowerCase();

        return (
          safe(a.nome).includes(term) ||
          safe(a.cognome).includes(term) ||
          safe(`${a.nome} ${a.cognome}`).includes(term) ||
          safe(a.indirizzo).includes(term) ||
          safe(a.comune).includes(term) ||
          safe(a.zona).includes(term) ||
          safe(a.utenteZona).includes(term) ||
          safe(a.cap).includes(term)
        );
      })
      .filter((a) => !selZonaId || a.zonaId === selZonaId)
      .filter((a) => !selectedUser || a.utenteZona === selectedUser);
  }, [
    appuntamenti,
    caps.restrictAssignedOnly,
    UID,
    filterText,
    selectedZona,
    selectedUser,
  ]);

  /* ===== 6) Ordinamento ===== */

  const handleSort = (key) => {
    setSortKey((prevKey) => {
      if (prevKey === key) {
        setSortDir((prevDir) => (prevDir === 'asc' ? 'desc' : 'asc'));
        return prevKey;
      }
      setSortDir('asc');
      return key;
    });
  };

  const sortedAppointments = useMemo(() => {
    const arr = [...visibleAppointments];
    const dir = sortDir === 'desc' ? -1 : 1;

    arr.sort((a, b) => {
      const cmp = (va, vb) => {
        if (va < vb) return -1 * dir;
        if (va > vb) return 1 * dir;
        return 0;
      };

      switch (sortKey) {
        case 'data': {
          const ka = `${a.data || ''} ${a.ora || ''}`;
          const kb = `${b.data || ''} ${b.ora || ''}`;
          return cmp(ka, kb);
        }
        case 'ora':
          return cmp(a.ora || '', b.ora || '');
        case 'cliente': {
          const ca = `${a.nome} ${a.cognome}`.toLowerCase();
          const cb = `${b.nome} ${b.cognome}`.toLowerCase();
          return cmp(ca, cb);
        }
        case 'indirizzo':
          return cmp(
            (a.indirizzo || '').toLowerCase(),
            (b.indirizzo || '').toLowerCase()
          );
        case 'cap':
          return cmp(a.cap || '', b.cap || '');
        case 'comune':
          return cmp(
            (a.comune || '').toLowerCase(),
            (b.comune || '').toLowerCase()
          );
        case 'zona':
          return cmp(
            (a.zona || '').toLowerCase(),
            (b.zona || '').toLowerCase()
          );
        case 'utente':
          return cmp(
            (a.utenteZona || '').toLowerCase(),
            (b.utenteZona || '').toLowerCase()
          );
        default:
          return 0;
      }
    });

    return arr;
  }, [visibleAppointments, sortKey, sortDir]);

  // paginazione lato client
  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(sortedAppointments.length / perPage));
  }, [sortedAppointments.length, perPage]);

  const pageItems = useMemo(() => {
    const start = (page - 1) * perPage;
    return sortedAppointments.slice(start, start + perPage);
  }, [sortedAppointments, page, perPage]);

  // reset pagina quando cambiano i filtri principali
  useEffect(() => {
    setPage(1);
  }, [filterText, selectedZona, selectedUser]);

  // Link al preventivo ufficiale
  const openPreventivo = (preventivoId) => {
    if (!preventivoId) return;
    navigate(`/nuovo/${preventivoId}`);
  };

  // Link all'agendaPro sul giorno dell'appuntamento
  const openAgendaProDay = (data, id) => {
    if (!data) return;
    const qs = new URLSearchParams({ day: data });
    if (id) qs.set('event', String(id));
    navigate(`/agenda-pro?${qs.toString()}`);
  };

  // Log: modale interna (usa solo app.history, niente chiamata PHP)
  const openLog = (app) => {
    if (!app?.id) return;
    setLogEvent(app);
    setLogOpen(true);

    const hist = Array.isArray(app.history) ? app.history : [];

    setLogRows(hist);
    setLogError(null);
    setLogLoading(false);
  };

  // R012: controllo solo quando clicchi
  const openR012 = async (app) => {
    const id = app?.id;
    if (!id) return;

    try {
      const res = await fetch(
        `/preventivi/R012/exists.php?appointment_id=${encodeURIComponent(
          id
        )}`,
        { credentials: 'include' }
      );
      const j = await res.json().catch(() => ({}));

      if (j?.exists && j?.id) {
        // c'è già un R012 → view
        window.open(
          `/preventivi/R012/view.php?id=${encodeURIComponent(j.id)}`,
          '_blank',
          'noopener,noreferrer'
        );
      } else {
        // non esiste → apri form precompilato
        const qs = new URLSearchParams({
          appointment_id: String(id),
          cliente_nome: `${app.nome || ''} ${app.cognome || ''}`.trim(),
          indirizzo: app.indirizzo || '',
          data_appuntamento: app.data || '',
          ora_appuntamento: app.ora || '',
        });
        window.open(
          `/preventivi/R012/form.php?${qs.toString()}`,
          '_blank',
          'noopener,noreferrer'
        );
      }
    } catch (err) {
      console.error('Errore R012:', err);
      alert('R012 non disponibile (errore di connessione).');
    }
  };

  const sortClass = (key) =>
    `sortable${sortKey === key ? ` sorted-${sortDir}` : ''}`;

  return (
    <div className="elenco-container">
      {(loading || !capsLoaded) && (
        <div className="App">Caricamento…</div>
      )}
      {error && <div className="App error">Errore: {error}</div>}

      {!loading && capsLoaded && !error && (
        <div className="App elenco-page">
          <h1 className="elenco-titolo">Elenco Appuntamenti</h1>

          {/* Filtri in alto: ricerca + zona + utente */}
          <div className="filters single-row">
            <input
              type="text"
              placeholder="Cerca per cliente, indirizzo o comune…"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
            />

            <select
              value={selectedZona}
              onChange={(e) => {
                setSelectedZona(e.target.value);
                setSelectedUser('');
              }}
              disabled={caps.zoneLock && !!userZonaId}
              title={
                caps.zoneLock && userZonaId
                  ? 'Zona bloccata dal tuo profilo'
                  : 'Filtra per zona'
              }
            >
              <option value="">Tutte le zone</option>
              {zones.map((z) => (
                <option key={z.id} value={String(z.id)}>
                  {z.label}
                </option>
              ))}
            </select>

            <select
              value={selectedUser}
              onChange={(e) => {
                setSelectedUser(e.target.value);
              }}
              disabled={usersInSelectedZone.length === 0}
            >
              <option value="">Tutti gli utenti</option>
              {usersInSelectedZone.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          </div>

          <div className="table-wrapper glassmorphism">
            <table className="elenco-table">
              <thead>
                <tr>
                  <th
                    className={sortClass('data')}
                    onClick={() => handleSort('data')}
                  >
                    Data
                  </th>
                  <th
                    className={sortClass('ora')}
                    onClick={() => handleSort('ora')}
                  >
                    Ora
                  </th>
                  <th
                    className={sortClass('cliente')}
                    onClick={() => handleSort('cliente')}
                  >
                    Cliente
                  </th>
                  <th
                    className={sortClass('indirizzo')}
                    onClick={() => handleSort('indirizzo')}
                  >
                    Indirizzo
                  </th>
                  <th
                    className={sortClass('cap')}
                    onClick={() => handleSort('cap')}
                  >
                    CAP
                  </th>
                  <th
                    className={sortClass('comune')}
                    onClick={() => handleSort('comune')}
                  >
                    Comune
                  </th>
                  <th
                    className={sortClass('zona')}
                    onClick={() => handleSort('zona')}
                  >
                    Zona
                  </th>
                  <th
                    className={sortClass('utente')}
                    onClick={() => handleSort('utente')}
                  >
                    Utente
                  </th>
                  <th>Prev.</th>
                  <th>Audio</th>
                  <th>R012</th>
                  <th>Agenda</th>
                  <th>Log</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.length === 0 ? (
                  <tr>
                    <td colSpan={13} className="no-results">
                      Nessun appuntamento trovato.
                    </td>
                  </tr>
                ) : (
                  pageItems.map((a) => {
                    const statusKey = (a.status || 'default')
                      .toString()
                      .toLowerCase()
                      .replace(/\s+/g, '-');

                    return (
                      <tr
                        key={a.id}
                        className={`fade-in appointment-row status-${statusKey}`}
                      >
                        <td>{a.data}</td>
                        <td>{a.ora}</td>
                        <td>{`${a.nome} ${a.cognome}`.trim()}</td>
                        <td>{a.indirizzo}</td>
                        <td>{a.cap}</td>
                        <td>{a.comune}</td>
                        <td>{a.zona}</td>
                        <td>{a.utenteZona}</td>

                        {/* link al preventivo ufficiale */}
                        <td>
                          {a.preventivoId && (
                            <button
                              className="icon-button"
                              title="Apri preventivo ufficiale"
                              onClick={() => openPreventivo(a.preventivoId)}
                            >
                              🧾
                            </button>
                          )}
                        </td>

                        {/* link audio */}
                        <td>
                          {a.audioUrl && (
                            <a
                              href={a.audioUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="icon-button"
                              title="Ascolta audio"
                            >
                              🔊
                            </a>
                          )}
                        </td>

                        {/* R012: sempre visibile, controllo alla pressione */}
                        <td>
                          {a.id && (
                            <button
                              className="icon-button"
                              title="Apri / compila R012"
                              onClick={() => openR012(a)}
                            >
                              📄
                            </button>
                          )}
                        </td>

                        {/* AgendaPro sul giorno */}
                        <td>
                          {a.data && (
                            <button
                              className="icon-button"
                              title="Apri giorno in AgendaPro"
                              onClick={() =>
                                openAgendaProDay(a.data, a.id)
                              }
                            >
                              📅
                            </button>
                          )}
                        </td>

                        {/* Log → modale interna */}
                        <td>
                          <button
                            className="icon-button"
                            title="Log modifiche appuntamento"
                            onClick={() => openLog(a)}
                          >
                            🕑
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="elenco-pagination">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              ‹ Prev
            </button>
            <span>
              Pagina {page} di {totalPages}
            </span>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next ›
            </button>
          </div>

          {/* ===== MODALE LOG ===== */}
          {logOpen && (
            <div
              className="log-modal-backdrop"
              onClick={() => setLogOpen(false)}
            >
              <div
                className="log-modal"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="log-modal-header">
                  <div className="log-modal-title">
                    Log appuntamento #{logEvent?.id}{' '}
                    {logEvent &&
                      `· ${logEvent.data || ''} ${logEvent.ora || ''} · ${
                        logEvent.nome || ''
                      } ${logEvent.cognome || ''}`}
                  </div>
                  <button
                    className="log-modal-close"
                    onClick={() => setLogOpen(false)}
                    title="Chiudi"
                  >
                    ✕
                  </button>
                </div>

                <div className="log-modal-body">
                  {logLoading && <div>Caricamento log…</div>}
                  {logError && (
                    <div className="log-error">{logError}</div>
                  )}

                  {!logLoading && !logError && logRows.length === 0 && (
                    <div>Nessuna modifica registrata.</div>
                  )}

                  {!logLoading && !logError && logRows.length > 0 && (
                    <ul className="log-list">
                      {logRows.map((row, idx) => (
                        <li key={idx} className="log-row">
                          <div className="log-when">
                            {String(
                              row.at ||
                                row.when ||
                                row.updated_at ||
                                row.created_at ||
                                row.timestamp ||
                                ''
                            ).replace('T', ' ')}
                          </div>
                          <div className="log-who">
                            {row.by_name ||
                              row.by ||
                              row.user ||
                              row.operator ||
                              row.username ||
                              '—'}
                          </div>
                          <div className="log-what">
                            {row.what ||
                              row.action ||
                              row.note ||
                              row.description ||
                              row.msg ||
                              '—'}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
